import React, { useState, useEffect, useRef, useCallback } from "react";
import { io, Socket } from "socket.io-client";
import { wsLogger } from "../../utils/socketLogger";

type BulkSenderProps = { authId: string };

interface FailedNumber {
  number: string;
  error: string;
  attempts: number;
}

interface BulkProgress {
  current: number;
  attempted: number;
  total: number;
  percentage: number;
  status: string;
  successfulNumbers: string[];
  failedNumbers: FailedNumber[];
}

interface BulkComplete {
  authId: string;
  total: number;
  sent: number;
  failed: number;
  successfulNumbers: string[];
  failedNumbers: FailedNumber[];
  error?: string;
}

const SOCKET_SERVER_URL = (import.meta as any).env.VITE_BACKEND_URL;

const BulkSender: React.FC<BulkSenderProps> = ({ authId }) => {
  const [numbersText, setNumbersText] = useState("");
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [messages, setMessages] = useState<string[]>([""]);

  // Estado do envio
  const [sending, setSending] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [httpError, setHttpError] = useState<string | null>(null);

  // Progresso em tempo real
  const [progress, setProgress] = useState(0);
  const [sentCount, setSentCount] = useState(0);
  const [attempted, setAttempted] = useState(0);
  const [total, setTotal] = useState(0);
  const [currentStatus, setCurrentStatus] = useState("");
  const [successfulNumbers, setSuccessfulNumbers] = useState<string[]>([]);
  const [failedNumbers, setFailedNumbers] = useState<FailedNumber[]>([]);
  const [startTime, setStartTime] = useState(0);

  // UI
  const [showFailedDetails, setShowFailedDetails] = useState(true);
  const [showSuccessDetails, setShowSuccessDetails] = useState(false);

  const socketRef = useRef<Socket | null>(null);

  // Conectar socket quando envio começa
  useEffect(() => {
    if (!sending) return;

    const socket = io(SOCKET_SERVER_URL, {
      query: { authId },
      withCredentials: true,
      transports: ['websocket', 'polling'],
      timeout: 30000,
    });
    socketRef.current = socket;

    socket.on('connect', () => {
      wsLogger.connected(socket.id || '', authId, Date.now());
    });

    socket.on("bulk-progress", (data: BulkProgress) => {
      wsLogger.eventReceived('bulk-progress', data, authId);
      setTotal(data.total);
      setAttempted(data.attempted);
      setSentCount(data.current);
      setProgress(data.percentage);
      setCurrentStatus(data.status);
      setSuccessfulNumbers(data.successfulNumbers || []);
      setFailedNumbers(data.failedNumbers || []);
    });

    socket.on("bulk-complete", (data: BulkComplete) => {
      wsLogger.eventReceived('bulk-complete', data, authId);
      setSentCount(data.sent);
      setTotal(data.total);
      setSuccessfulNumbers(data.successfulNumbers || []);
      setFailedNumbers(data.failedNumbers || []);
      setProgress(100);
      setCurrentStatus(data.error || (data.failed === 0
        ? `✅ Envio concluído com sucesso!`
        : `⚠️ Envio concluído com ${data.failed} falha(s)`));
      setIsComplete(true);
      setSending(false);
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [sending, authId]);

  const resetState = () => {
    setIsComplete(false);
    setHttpError(null);
    setProgress(0);
    setSentCount(0);
    setAttempted(0);
    setTotal(0);
    setCurrentStatus("");
    setSuccessfulNumbers([]);
    setFailedNumbers([]);
    setShowFailedDetails(true);
    setShowSuccessDetails(false);
  };

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    resetState();
    setSending(true);
    setStartTime(Date.now());
    setCurrentStatus("Iniciando envio...");

    try {
      const formData = new FormData();
      formData.append("authId", authId);
      if (csvFile) {
        formData.append("file", csvFile);
      } else {
        formData.append("numbers", numbersText);
      }
      const filteredMessages = messages.filter(m => m.trim() !== "");
      formData.append("messages", JSON.stringify(filteredMessages));

      const res = await fetch(`${SOCKET_SERVER_URL}/api/messaging/send-bulk`, {
        method: "POST",
        body: formData,
      });

      const data = await res.json();

      if (!data.success) {
        setSending(false);
        setHttpError(data.error || "Erro desconhecido");
      }
      // Se success, NÃO fazer setSending(false) — o socket vai controlar
    } catch (err) {
      setSending(false);
      setHttpError((err as any).message || "Erro de conexão com o servidor");
    }
  };

  const handleRetryFailed = useCallback(() => {
    const failedNums = failedNumbers.map(f => f.number).join("\n");
    setNumbersText(failedNums);
    setCsvFile(null);
    resetState();
    // Scroll para o topo
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [failedNumbers]);

  const handleMessageChange = (idx: number, value: string) => {
    setMessages(msgs => msgs.map((m, i) => (i === idx ? value : m)));
  };

  const handleAddMessage = () => {
    setMessages(msgs => [...msgs, ""]);
  };

  const handleRemoveMessage = (idx: number) => {
    setMessages(msgs => msgs.length > 1 ? msgs.filter((_, i) => i !== idx) : msgs);
  };

  const showProgress = sending || isComplete;
  const failedCount = failedNumbers.length;

  // ETA calculation
  const etaText = (() => {
    if (!sending || progress <= 0 || progress >= 100 || startTime <= 0) return null;
    const elapsed = (Date.now() - startTime) / 1000;
    const rate = progress / elapsed;
    if (rate <= 0) return null;
    const remaining = Math.round((100 - progress) / rate);
    if (remaining <= 0) return null;
    if (remaining > 60) return `~${Math.round(remaining / 60)}min`;
    return `~${remaining}s`;
  })();

  return (
    <div className="wa-chat-workspace wa-animate-fade-in wa-theme-bulk">
      {/* Header */}
      <header className="wa-workspace-header">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="wa-heading-3" style={{ color: 'var(--wa-gray-900)', marginBottom: 'var(--wa-space-1)' }}>
              📤 Envio em Massa
            </h1>
            <p style={{ fontSize: 'var(--wa-text-sm)', color: 'var(--wa-gray-600)' }}>
              Envie mensagens para múltiplos números de telefone
            </p>
          </div>
        </div>
      </header>

      <div className="wa-workspace-simple">
        <form onSubmit={handleSend}>
          {/* Numbers Section */}
          <div className="wa-section-card wa-animate-slide-up" style={{ animationDelay: '100ms' }}>
            <div className="wa-section-header">
              <h3 className="wa-panel-title">
                <span style={{ fontSize: 'var(--wa-text-lg)' }}>📱</span>
                <span>1. Adicionar Números</span>
              </h3>
              <p className="wa-section-instructions">
                📝 <strong>Digite ou envie um arquivo</strong> com os números de telefone (um por linha)
              </p>
            </div>
            <div className="wa-input-group">
              <label className="wa-element-label">
                <span>📝</span>
                <span>Lista de Números</span>
              </label>
              <textarea
                value={numbersText}
                onChange={e => setNumbersText(e.target.value)}
                rows={6}
                className="wa-composer-textarea"
                disabled={!!csvFile || sending}
                placeholder="📝 Digite os números aqui (um por linha)...

Exemplo:
5511999999999
5511888888888
5511777777777

💡 Dica: Use apenas números válidos com DDD"
                style={{
                  minHeight: '150px',
                  opacity: csvFile ? 0.5 : 1,
                  cursor: csvFile ? 'not-allowed' : 'text'
                }}
              />
              <div className="wa-textarea-footer">
                <div className="wa-char-count">
                  <span className="wa-text-muted">
                    {numbersText ? numbersText.split(/[,\s\n]+/).filter(n => n.trim()).length : 0} números
                  </span>
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--wa-space-3)', margin: 'var(--wa-space-4) 0' }}>
              <div style={{ flex: 1, height: '1px', background: 'var(--wa-gray-300)' }}></div>
              <span style={{ color: 'var(--wa-gray-500)', fontSize: 'var(--wa-text-sm)', fontWeight: 'var(--wa-font-medium)' }}>OU</span>
              <div style={{ flex: 1, height: '1px', background: 'var(--wa-gray-300)' }}></div>
            </div>

            <div className="wa-input-group">
              <label className="wa-element-label">
                <span>📄</span>
                <span>Arquivo CSV</span>
              </label>
              <div className="wa-file-upload-group">
                <input
                  type="file"
                  id="csv-upload"
                  accept=".csv,.txt"
                  onChange={e => setCsvFile(e.target.files ? e.target.files[0] : null)}
                  disabled={!!numbersText || sending}
                  style={{ display: 'none' }}
                />
                <button
                  type="button"
                  className={`wa-btn wa-btn-with-icon ${csvFile ? 'wa-btn-success' : 'wa-btn-outline'}`}
                  onClick={() => document.getElementById('csv-upload')?.click()}
                  disabled={!!numbersText || sending}
                  title="Clique para enviar um arquivo CSV com números"
                >
                  <span className="wa-btn-icon">📄</span>
                  <span>{csvFile ? 'CSV Anexado' : 'Escolher Arquivo CSV'}</span>
                </button>
                {csvFile && (
                  <button
                    type="button"
                    onClick={() => setCsvFile(null)}
                    className="wa-btn wa-btn-small wa-btn-ghost"
                    title="Remover arquivo CSV"
                    style={{ marginLeft: 'var(--wa-space-1)' }}
                  >
                    ✕
                  </button>
                )}
              </div>
              {csvFile && (
                <div className="wa-file-info" style={{ marginTop: 'var(--wa-space-2)' }}>
                  <span className="wa-file-icon">📄</span>
                  <span className="wa-file-name">{csvFile.name}</span>
                  <span className="wa-file-size">({Math.round(csvFile.size / 1024)} KB)</span>
                </div>
              )}
            </div>
          </div>

          {/* Messages Section */}
          <div className="wa-section-card wa-animate-slide-up" style={{ animationDelay: '200ms' }}>
            <div className="wa-section-header">
              <h3 className="wa-panel-title">
                <span style={{ fontSize: 'var(--wa-text-lg)' }}>💬</span>
                <span>2. Escrever Mensagens</span>
                <span className="wa-counter">({messages.filter(m => m.trim() !== "").length})</span>
              </h3>
              <p className="wa-section-instructions">
                ✏️ <strong>Digite suas mensagens</strong> que serão enviadas para todos os números
              </p>
            </div>

            <div className="wa-messages-container">
              {messages.map((msg, idx) => (
                <div key={idx} className="wa-input-group">
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <label className="wa-element-label">
                      <span>💬</span>
                      <span>Mensagem {idx + 1}</span>
                    </label>
                    {messages.length > 1 && (
                      <button
                        type="button"
                        onClick={() => handleRemoveMessage(idx)}
                        className="wa-btn wa-btn-small wa-btn-ghost"
                        title="Remover esta mensagem"
                      >
                        🗑️
                      </button>
                    )}
                  </div>
                  <textarea
                    value={msg}
                    onChange={e => handleMessageChange(idx, e.target.value)}
                    rows={3}
                    className="wa-composer-textarea"
                    required={idx === 0}
                    disabled={sending}
                    placeholder={`📝 Digite a mensagem ${idx + 1} aqui...`}
                    style={{ minHeight: '100px', resize: 'vertical' }}
                  />
                </div>
              ))}
            </div>

            <button
              type="button"
              onClick={handleAddMessage}
              className="wa-btn wa-btn-outline wa-btn-with-icon"
              disabled={sending}
              style={{ marginTop: 'var(--wa-space-3)' }}
            >
              <span className="wa-btn-icon">➕</span>
              <span>Adicionar Nova Mensagem</span>
            </button>
          </div>

          {/* Action Bar */}
          <div className="wa-action-bar wa-animate-slide-up" style={{ animationDelay: '300ms' }}>
            <div className="wa-action-header">
              <span className="wa-step-number">3.</span>
              <span className="wa-step-title">Confirmar e Enviar</span>
            </div>

            <div className="wa-action-content">
              <div className="wa-selection-summary">
                {(!numbersText && !csvFile) ? (
                  <div className="wa-warning-message">
                    ⚠️ <strong>Adicione números</strong> digitando ou enviando um arquivo CSV
                  </div>
                ) : (
                  <div className="wa-success-message">
                    ✅ <strong>{numbersText ? numbersText.split(/[,\s\n]+/).filter(n => n.trim()).length : 'CSV'} números carregados</strong>
                  </div>
                )}

                {messages.every(m => m.trim() === "") && (numbersText || csvFile) && (
                  <div className="wa-warning-message">
                    ⚠️ <strong>Digite pelo menos uma mensagem</strong> para enviar
                  </div>
                )}

                {messages.filter(m => m.trim() !== "").length > 0 && (
                  <div className="wa-success-message">
                    💬 <strong>{messages.filter(m => m.trim() !== "").length} mensagens prontas</strong>
                  </div>
                )}
              </div>

              <button
                type="submit"
                className={`wa-send-button ${sending || (!numbersText && !csvFile) || messages.every(m => m.trim() === "") ? 'disabled' : 'ready'}`}
                disabled={sending || (!numbersText && !csvFile) || messages.every(m => m.trim() === "")}
              >
                <span className="wa-btn-icon">{sending ? '⏳' : '📤'}</span>
                <span className="wa-btn-text">
                  {sending ? 'Enviando...' :
                   (!numbersText && !csvFile) || messages.every(m => m.trim() === "") ? 'Preencha os campos acima' :
                   'Enviar para Todos os Números'}
                </span>
              </button>
            </div>
          </div>
        </form>

        {/* Erro HTTP */}
        {httpError && (
          <div className="wa-section-card">
            <div style={{
              padding: 'var(--wa-space-4)',
              background: 'var(--wa-error-50)',
              borderRadius: 'var(--wa-rounded-lg)',
              border: '1px solid var(--wa-error-200)',
              color: 'var(--wa-error-800)',
              fontSize: 'var(--wa-text-sm)',
              fontWeight: 'var(--wa-font-medium)'
            }}>
              ❌ {httpError}
            </div>
          </div>
        )}

        {/* Progresso em Tempo Real */}
        {showProgress && (
          <div className="wa-section-card">
            <h3 className="wa-panel-title">
              <span style={{ fontSize: 'var(--wa-text-lg)' }}>📊</span>
              {isComplete ? 'Resultado do Envio' : 'Progresso do Envio'}
            </h3>

            {/* Barra de progresso */}
            <div className="wa-progress-container">
              <div className="wa-progress-bar-wrapper">
                <div className="wa-progress-bar">
                  <div
                    className="wa-progress-fill"
                    style={{
                      width: `${progress}%`,
                      background: isComplete && failedCount > 0 && sentCount === 0
                        ? 'var(--wa-error-500)'
                        : isComplete && failedCount > 0
                        ? 'var(--wa-warning-500)'
                        : undefined,
                      transition: 'width 0.3s ease'
                    }}
                  />
                </div>
                <div className="wa-progress-text">
                  {progress}%
                </div>
              </div>
            </div>

            {/* Contadores */}
            <div style={{
              display: 'flex',
              gap: 'var(--wa-space-3)',
              marginTop: 'var(--wa-space-3)',
              flexWrap: 'wrap'
            }}>
              <div style={{
                flex: 1,
                minWidth: '120px',
                padding: 'var(--wa-space-3)',
                background: 'var(--wa-success-50)',
                borderRadius: 'var(--wa-rounded-md)',
                border: '1px solid var(--wa-success-200)',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: 'var(--wa-text-2xl)', fontWeight: 'var(--wa-font-bold)', color: 'var(--wa-success-700)' }}>
                  {sentCount}
                </div>
                <div style={{ fontSize: 'var(--wa-text-xs)', color: 'var(--wa-success-600)' }}>
                  ✅ Enviados
                </div>
              </div>

              <div style={{
                flex: 1,
                minWidth: '120px',
                padding: 'var(--wa-space-3)',
                background: failedCount > 0 ? 'var(--wa-error-50)' : 'var(--wa-gray-50)',
                borderRadius: 'var(--wa-rounded-md)',
                border: `1px solid ${failedCount > 0 ? 'var(--wa-error-200)' : 'var(--wa-gray-200)'}`,
                textAlign: 'center'
              }}>
                <div style={{
                  fontSize: 'var(--wa-text-2xl)',
                  fontWeight: 'var(--wa-font-bold)',
                  color: failedCount > 0 ? 'var(--wa-error-700)' : 'var(--wa-gray-400)'
                }}>
                  {failedCount}
                </div>
                <div style={{ fontSize: 'var(--wa-text-xs)', color: failedCount > 0 ? 'var(--wa-error-600)' : 'var(--wa-gray-500)' }}>
                  ❌ Falharam
                </div>
              </div>

              <div style={{
                flex: 1,
                minWidth: '120px',
                padding: 'var(--wa-space-3)',
                background: 'var(--wa-gray-50)',
                borderRadius: 'var(--wa-rounded-md)',
                border: '1px solid var(--wa-gray-200)',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: 'var(--wa-text-2xl)', fontWeight: 'var(--wa-font-bold)', color: 'var(--wa-gray-700)' }}>
                  {total}
                </div>
                <div style={{ fontSize: 'var(--wa-text-xs)', color: 'var(--wa-gray-500)' }}>
                  📊 Total
                </div>
              </div>
            </div>

            {/* Status atual + ETA */}
            {currentStatus && (
              <div className="wa-progress-status" style={{ marginTop: 'var(--wa-space-3)' }}>
                {currentStatus}
                {etaText && <span style={{ marginLeft: 'var(--wa-space-2)', color: 'var(--wa-gray-500)' }}>⏱️ {etaText}</span>}
              </div>
            )}
          </div>
        )}

        {/* Lista de Falhas */}
        {failedCount > 0 && (
          <div className="wa-section-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 className="wa-panel-title" style={{ margin: 0 }}>
                <span style={{ fontSize: 'var(--wa-text-lg)' }}>❌</span>
                Números que Falharam ({failedCount})
              </h3>
              <div style={{ display: 'flex', gap: 'var(--wa-space-2)' }}>
                <button
                  type="button"
                  className="wa-btn wa-btn-small wa-btn-ghost"
                  onClick={() => setShowFailedDetails(!showFailedDetails)}
                >
                  {showFailedDetails ? '▲ Ocultar' : '▼ Expandir'}
                </button>
                {isComplete && (
                  <button
                    type="button"
                    className="wa-btn wa-btn-small wa-btn-outline"
                    onClick={handleRetryFailed}
                    title="Preenche a lista de números com os que falharam para reenviar"
                    style={{ color: 'var(--wa-error-600)', borderColor: 'var(--wa-error-300)' }}
                  >
                    🔄 Reenviar Falhados
                  </button>
                )}
              </div>
            </div>

            {showFailedDetails && (
              <div style={{
                maxHeight: '300px',
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--wa-space-2)',
                marginTop: 'var(--wa-space-3)'
              }}>
                {failedNumbers.map((fail, i) => (
                  <div key={i} style={{
                    padding: 'var(--wa-space-3)',
                    background: 'var(--wa-error-50)',
                    borderRadius: 'var(--wa-rounded-md)',
                    border: '1px solid var(--wa-error-200)',
                    fontSize: 'var(--wa-text-sm)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--wa-space-2)'
                  }}>
                    <span>📱</span>
                    <span style={{ flex: 1, fontWeight: 'var(--wa-font-medium)', fontFamily: 'monospace' }}>{fail.number}</span>
                    <span style={{ color: 'var(--wa-error-600)', fontSize: 'var(--wa-text-xs)' }}>
                      {fail.error} ({fail.attempts} tent.)
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Lista de Sucessos (colapsável) */}
        {isComplete && successfulNumbers.length > 0 && (
          <div className="wa-section-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 className="wa-panel-title" style={{ margin: 0 }}>
                <span style={{ fontSize: 'var(--wa-text-lg)' }}>✅</span>
                Números Enviados ({successfulNumbers.length})
              </h3>
              <button
                type="button"
                className="wa-btn wa-btn-small wa-btn-ghost"
                onClick={() => setShowSuccessDetails(!showSuccessDetails)}
              >
                {showSuccessDetails ? '▲ Ocultar' : '▼ Expandir'}
              </button>
            </div>

            {showSuccessDetails && (
              <div style={{
                maxHeight: '200px',
                overflowY: 'auto',
                display: 'flex',
                flexWrap: 'wrap',
                gap: 'var(--wa-space-2)',
                marginTop: 'var(--wa-space-3)'
              }}>
                {successfulNumbers.map((num, i) => (
                  <span key={i} style={{
                    padding: 'var(--wa-space-1) var(--wa-space-2)',
                    background: 'var(--wa-success-50)',
                    borderRadius: 'var(--wa-rounded-md)',
                    border: '1px solid var(--wa-success-200)',
                    fontSize: 'var(--wa-text-xs)',
                    fontFamily: 'monospace',
                    color: 'var(--wa-success-700)'
                  }}>
                    {num}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default BulkSender;
